import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { z } from "zod";
import { fetchCustomerByIdQueryOptions } from "@/api/customers";
import { fetchCustomerFutureSessionsQueryOptions } from "@/api/scheduleSessionAttendance";
import { Card, CardContent } from "@/components/ui/card";
import { Pagination } from "@/components/ui/pagination";
import { useApiParameters } from "@/hooks/useApiParameters";
import { CustomerSessionList } from "@/routes/_auth/app/_app/org/$orgId/customers/-components/CustomerSessionList";

// Search params schema
const sessionsSearchSchema = z.object({
	page: z.number().min(1).catch(1),
	limit: z.number().min(1).max(100).catch(10),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customers/$customerId/sessions",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, customerId } = params;

		// Preload customer data
		context.queryClient.ensureQueryData(
			fetchCustomerByIdQueryOptions(userId, token, orgId, customerId),
		);

		// Preload customer future sessions data
		context.queryClient.ensureQueryData(
			fetchCustomerFutureSessionsQueryOptions(
				userId,
				token,
				orgId,
				customerId,
				{
					page: 1,
					limit: 10,
				},
			),
		);

		return { userId, token, orgId, customerId };
	},
	validateSearch: sessionsSearchSchema,
	component: CustomerSessionsPage,
});

function CustomerSessionsPage() {
	const { orgId, customerId } = Route.useParams();
	const { userId, token } = Route.useLoaderData();
	const search = Route.useSearch();

	// Local state for pagination
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});

	// Pagination handlers
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (limit: number) => {
		setPageSize(limit);
		setCurrentPage(1); // Reset to first page when changing page size
	};

	const handlePaginationMetaChange = useCallback(
		(meta: {
			page: number;
			limit: number;
			total: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		}) => {
			setPaginationMeta(meta);
		},
		[],
	);

	return (
		<div className="h-full flex flex-col space-y-6">
			<Card className="flex-1 flex flex-col overflow-hidden my-3 py-3">
				<CardContent className="flex-1 overflow-hidden flex flex-col space-y-3">
					{/* Table (desktop) / card list (mobile) */}
					<div className="flex-1 overflow-hidden">
						<CustomerSessionList
							organizationId={orgId}
							customerId={customerId}
							pagination={{
								page: currentPage,
								limit: pageSize,
							}}
							userId={userId}
							token={token}
							onPaginationMetaChange={handlePaginationMetaChange}
						/>
					</div>

					{/* Pagination */}
					<div className="shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
